import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { ReactComponent as Plus } from '../../../assets/icons/plus.svg';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image.svg';
import { ReactComponent as ExternalLinkIcon } from '../../../assets/icons/external-link.svg';
import { CreateBusinessDraftModal } from '../components/CreateBusinessDraftModal';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { Toast } from '../../../components/Toast';
import { BusinessOnboardingService, BusinessAccountService } from '../../../api/generated';
import type { DraftBusinessApplicationListItem } from '../../../api/generated/models/DraftBusinessApplicationListItem';
import type { BusinessApplicationInReviewListItem } from '../../../api/generated/models/BusinessApplicationInReviewListItem';
import type { BusinessViewModel } from '../../../api/generated/models/BusinessViewModel';
import { ROUTES } from '../../../config/routes';

const FRONTEND_BASE_URL =
  process.env.REACT_APP_FRONTEND_BASE_URL || 'http://localhost:3000';

interface LocationState {
  errorMessage?: string;
  successMessage?: string;
}

const PromotionsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<DraftBusinessApplicationListItem[]>([]);
  const [inReviewApplications, setInReviewApplications] = useState<BusinessApplicationInReviewListItem[]>([]);
  const [publishedBusinesses, setPublishedBusinesses] = useState<BusinessViewModel[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    draftId: string | null;
    draftName: string | null;
  }>({ isOpen: false, draftId: null, draftName: null });
  const location = useLocation();
  const navigate = useNavigate();
  const popoverRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const fetchBusinessApplications = async () => {
    try {
      setIsLoading(true);
      setFetchError(false);
      const [draftsResponse, inReviewResponse, publishedResponse, companyResponse] = await Promise.all([
        BusinessOnboardingService.getApiBusinessOnboardingListDraftBusinessApplications(),
        BusinessOnboardingService.getApiBusinessOnboardingListBusinessApplicationsInReview(),
        BusinessAccountService.postApiBusinessAccountListBusinesses({}),
        BusinessAccountService.postApiBusinessAccountGetCompany({}),
      ]);
      setDrafts(draftsResponse || []);
      setInReviewApplications(inReviewResponse || []);
      setPublishedBusinesses(publishedResponse || []);
      setCompanyId(companyResponse?.id || null);
    } catch (error) {
      console.error('Error fetching business applications:', error);
      setFetchError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessApplications();
  }, []);

  // Handle messages from navigation state (e.g., draft not found, submission success)
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.errorMessage) {
      setErrorMessage(state.errorMessage);
    }
    if (state?.successMessage) {
      setSuccessMessage(state.successMessage);
    }
    // Clear the location state to prevent showing the message again on refresh
    if (state?.errorMessage || state?.successMessage) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openPopoverId) return;

      const popoverEl = popoverRefs.current.get(openPopoverId);
      const buttonEl = buttonRefs.current.get(openPopoverId);

      if (
        popoverEl &&
        !popoverEl.contains(event.target as Node) &&
        buttonEl &&
        !buttonEl.contains(event.target as Node)
      ) {
        setOpenPopoverId(null);
      }
    };

    if (openPopoverId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopoverId]);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handlePreview = (companyId: string, draftId: string) => {
    window.open(
      `${FRONTEND_BASE_URL}/promotion-preview/card-linking/${companyId}/${draftId}`,
      '_blank'
    );
    setOpenPopoverId(null);
  };

  const handleDeleteClick = (draftId: string, draftName: string | null) => {
    setDeleteConfirmation({
      isOpen: true,
      draftId,
      draftName,
    });
    setOpenPopoverId(null);
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.draftId) return;

    try {
      setIsDeleting(true);
      await BusinessOnboardingService.deleteApiBusinessOnboardingDeleteDraftBusinessApplication(
        deleteConfirmation.draftId
      );
      setDrafts((prev) =>
        prev.filter(
          (draft) =>
            draft.draftBusinessApplicationId !== deleteConfirmation.draftId
        )
      );
      setDeleteConfirmation({ isOpen: false, draftId: null, draftName: null });
    } catch (error) {
      console.error('Error deleting draft:', error);
      setErrorMessage('Failed to delete draft. Please try again.');
      setDeleteConfirmation({ isOpen: false, draftId: null, draftName: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, draftId: null, draftName: null });
  };

  return (
    <div>
      {errorMessage && (
        <Toast
          message={errorMessage}
          type="error"
          onClose={() => setErrorMessage(null)}
        />
      )}
      {successMessage && (
        <Toast
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />
      )}
      <div className="flex max-md:flex-col md:items-center md:justify-between mb-8 max-md:gap-4">
        <h2 className="font-medium">Your Businesses and Promotions</h2>
        {/* TODO: Unhide when feature is ready
        <Button
          onClick={() => setIsModalOpen(true)}
          variant="light"
          className="gap-2"
        >
          Register New Business
          <Plus />
        </Button>
        */}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <span className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary" />
        </div>
      ) : fetchError ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Failed to load businesses.</span>
            <button
              onClick={fetchBusinessApplications}
              className="font-medium underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : drafts.length === 0 && inReviewApplications.length === 0 && publishedBusinesses.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>You have not registered any business yet.</p>
          <p className="text-sm mt-2">
            Click "Register New Business" to register your first business.
          </p>
        </div>
      ) : (
        <>
          {/* Drafts Section */}
          {drafts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {drafts.map((draft) => (
                <div
                  key={draft.draftBusinessApplicationId}
                  onClick={() =>
                    navigate(
                      ROUTES.PROMOTIONS_DRAFT_EDIT(
                        draft.draftBusinessApplicationId!
                      )
                    )
                  }
                  className="relative bg-white rounded-2xl border border-gray-200 p-6 flex flex-col items-center transition hover:shadow-lg cursor-pointer"
                >
                  <span className="absolute top-4 left-4 px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                    DRAFT
                  </span>

                  {/* More Actions Button */}
                  <div className="absolute top-4 right-4">
                    <button
                      ref={(el) => {
                        if (el)
                          buttonRefs.current.set(
                            draft.draftBusinessApplicationId!,
                            el
                          );
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenPopoverId(
                          openPopoverId === draft.draftBusinessApplicationId
                            ? null
                            : draft.draftBusinessApplicationId!
                        );
                      }}
                      className="p-2 rounded-full hover:bg-gray-100 transition"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        className="text-gray-500"
                      >
                        <circle cx="8" cy="3" r="1.5" />
                        <circle cx="8" cy="8" r="1.5" />
                        <circle cx="8" cy="13" r="1.5" />
                      </svg>
                    </button>

                    {/* Popover Menu */}
                    {openPopoverId === draft.draftBusinessApplicationId && (
                      <div
                        ref={(el) => {
                          if (el)
                            popoverRefs.current.set(
                              draft.draftBusinessApplicationId!,
                              el
                            );
                        }}
                        className="absolute right-0 top-10 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() =>
                            handlePreview(draft.companyId!, draft.draftBusinessApplicationId!)
                          }
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                        >
                          <ExternalLinkIcon className="w-4 h-4" />
                          Preview
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteClick(
                              draft.draftBusinessApplicationId!,
                              draft.name || null
                            )
                          }
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600 flex items-center gap-2"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {draft.logoImage?.url ? (
                    <img
                      src={draft.logoImage.url}
                      alt={draft.name || 'Business logo'}
                      className="w-20 h-20 rounded-full object-cover mb-4 mt-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4 mt-4">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}

                  <h3 className="font-medium text-lg text-center line-clamp-2">
                    {draft.name || 'Untitled Business'}
                  </h3>
                </div>
              ))}
            </div>
          )}

          {/* In Review Section */}
          {inReviewApplications.length > 0 && (
            <div className={drafts.length > 0 ? 'mt-8' : ''}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {inReviewApplications.map((application) => (
                  <div
                    key={application.draftBusinessApplicationId}
                    className="relative bg-white rounded-2xl border border-gray-200 p-6 flex flex-col items-center"
                  >
                    <span className="absolute top-4 left-4 px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                      IN REVIEW
                    </span>

                    {/* More Actions Button (Preview only, no delete) */}
                    <div className="absolute top-4 right-4">
                      <button
                        ref={(el) => {
                          if (el)
                            buttonRefs.current.set(
                              `review-${application.draftBusinessApplicationId!}`,
                              el
                            );
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const reviewId = `review-${application.draftBusinessApplicationId!}`;
                          setOpenPopoverId(
                            openPopoverId === reviewId ? null : reviewId
                          );
                        }}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="text-gray-500"
                        >
                          <circle cx="8" cy="3" r="1.5" />
                          <circle cx="8" cy="8" r="1.5" />
                          <circle cx="8" cy="13" r="1.5" />
                        </svg>
                      </button>

                      {/* Popover Menu (Preview only) */}
                      {openPopoverId ===
                        `review-${application.draftBusinessApplicationId}` && (
                        <div
                          ref={(el) => {
                            if (el)
                              popoverRefs.current.set(
                                `review-${application.draftBusinessApplicationId!}`,
                                el
                              );
                          }}
                          className="absolute right-0 top-10 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() =>
                              handlePreview(
                                application.companyId!,
                                application.draftBusinessApplicationId!
                              )
                            }
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                          >
                            <ExternalLinkIcon className="w-4 h-4" />
                            Preview
                          </button>
                        </div>
                      )}
                    </div>

                    {application.logoImage?.url ? (
                      <img
                        src={application.logoImage.url}
                        alt={application.name || 'Business logo'}
                        className="w-20 h-20 rounded-full object-cover mb-4 mt-4"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4 mt-4">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    <h3 className="font-medium text-lg text-center line-clamp-2">
                      {application.name || 'Untitled Business'}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Published Section */}
          {publishedBusinesses.length > 0 && (
            <div className={drafts.length > 0 || inReviewApplications.length > 0 ? 'mt-8' : ''}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {publishedBusinesses.map((business) => (
                  <div
                    key={business.id}
                    className="relative bg-white rounded-2xl border border-gray-200 p-6 flex flex-col items-center"
                  >
                    <span className="absolute top-4 left-4 px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      PUBLISHED
                    </span>

                    {/* More Actions Button (Preview only) */}
                    <div className="absolute top-4 right-4">
                      <button
                        ref={(el) => {
                          if (el)
                            buttonRefs.current.set(
                              `published-${business.id!}`,
                              el
                            );
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const publishedId = `published-${business.id!}`;
                          setOpenPopoverId(
                            openPopoverId === publishedId ? null : publishedId
                          );
                        }}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="text-gray-500"
                        >
                          <circle cx="8" cy="3" r="1.5" />
                          <circle cx="8" cy="8" r="1.5" />
                          <circle cx="8" cy="13" r="1.5" />
                        </svg>
                      </button>

                      {/* Popover Menu */}
                      {openPopoverId === `published-${business.id}` && (
                        <div
                          ref={(el) => {
                            if (el)
                              popoverRefs.current.set(
                                `published-${business.id!}`,
                                el
                              );
                          }}
                          className="absolute right-0 top-10 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => {
                              if (companyId && business.id) {
                                window.open(
                                  `${FRONTEND_BASE_URL}/promotion-preview/card-linking/${companyId}/${business.id}?published=true`,
                                  '_blank'
                                );
                              }
                              setOpenPopoverId(null);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                          >
                            <ExternalLinkIcon className="w-4 h-4" />
                            Preview
                          </button>
                          <button
                            onClick={() => {
                              window.open(
                                `${FRONTEND_BASE_URL}/?search=${encodeURIComponent(business.name || '')}`,
                                '_blank'
                              );
                              setOpenPopoverId(null);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                          >
                            <ExternalLinkIcon className="w-4 h-4" />
                            See Live
                          </button>
                        </div>
                      )}
                    </div>

                    {business.logo?.url ? (
                      <img
                        src={business.logo.url}
                        alt={business.name || 'Business logo'}
                        className="w-20 h-20 rounded-full object-cover mb-4 mt-4"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4 mt-4">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}

                    <h3 className="font-medium text-lg text-center line-clamp-2">
                      {business.name || 'Untitled Business'}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <CreateBusinessDraftModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Draft"
        message={`Are you sure you want to delete "${deleteConfirmation.draftName || 'this draft'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PromotionsPage;
